import { z } from "zod";

import { guardNever } from "../errors.js";
import { zGuard } from "../zod/utils.js";
import { getNormalEnum } from "../pseudoEnums.js";

export const UnaryColumnPredicateOpLiteral = z.enum([
  "IS_TRUE",
  "IS_FALSE",
  "ALWAYS",
  "IS_NULL",
  "NOT_NULL",
]);
export type UnaryColumnPredicateOp = z.infer<
  typeof UnaryColumnPredicateOpLiteral
>;
export const UnaryColumnPredicateOp = getNormalEnum(
  UnaryColumnPredicateOpLiteral,
);
export const UnaryColumnPredicate = z.object({
  op: UnaryColumnPredicateOpLiteral,
});
export type UnaryColumnPredicate = z.infer<typeof UnaryColumnPredicate>;

export const BinaryColumnPredicateOpLiteral = z.enum([
  "GT",
  "GTE",
  "EQ",
  "NEQ",
  "LTE",
  "LT",
  "CONTAINS",
  "NOT_CONTAINS",
  "DATE_BEFORE",
  "DATE_EQUAL_OR_BEFORE",
  "DATE_EQUAL",
  "DATE_NOT_EQUAL",
  "DATE_AFTER",
  "DATE_EQUAL_OR_AFTER",
]);
export type BinaryColumnPredicateOp = z.infer<
  typeof BinaryColumnPredicateOpLiteral
>;
export const BinaryColumnPredicateOp = getNormalEnum(
  BinaryColumnPredicateOpLiteral,
);

export const ListBinaryColumnPredicateOpLiteral = z.enum([
  "IS_ONE_OF",
  "NOT_ONE_OF",
  "DATE_BETWEEN",
]);
export type ListBinaryColumnPredicateOp = z.infer<
  typeof ListBinaryColumnPredicateOpLiteral
>;
export const ListBinaryColumnPredicateOp = getNormalEnum(
  ListBinaryColumnPredicateOpLiteral,
);

export const BinaryColumnPredicate = z.object({
  op: BinaryColumnPredicateOpLiteral,
  // arg is typically evaluated as a Jinja template, though see InternalBinaryPredicate below
  arg: z.string(),
  // `allowInterpolation` is omitted on the persisted db representation and added when
  // passed them into the kernel. In practice this is used to to determine whether to
  // support jinja templating in filters.
  allowInterpolation: z.boolean().optional(),
});
export type BinaryColumnPredicate = z.infer<typeof BinaryColumnPredicate>;

export const ListBinaryColumnPredicate = z.object({
  op: ListBinaryColumnPredicateOpLiteral,
  arg: z.array(z.string()),
  // `allowInterpolation` is omitted on the persisted db representation and added when
  // passed them into the kernel. In practice this is used to to determine whether to
  // support jinja templating in filters.
  allowInterpolation: z.boolean().optional(),
});
export type ListBinaryColumnPredicate = z.infer<
  typeof ListBinaryColumnPredicate
>;

export const CompoundColumnPredicateOpLiteral = z.enum(["AND", "OR"]);
export type CompoundColumnPredicateOp = z.infer<
  typeof CompoundColumnPredicateOpLiteral
>;
export const CompoundColumnPredicateOp = getNormalEnum(
  CompoundColumnPredicateOpLiteral,
);

export const LumpColumnPredicateOpLiteral = z.enum([
  "TOP_N",
  "BOTTOM_N",
  "NOT_TOP_N",
  "NOT_BOTTOM_N",
]);
export type LumpColumnPredicateOp = z.infer<
  typeof LumpColumnPredicateOpLiteral
>;
export const LumpColumnPredicateOp = getNormalEnum(
  LumpColumnPredicateOpLiteral,
);

/**
 * LumpColumnPredicates use the hex-sl Lump API to group, rank, and filter
 * data based on window functions and custom grouping logic.
 *
 * We don't allow jinja templating in lump column predicates.
 */
export const LumpColumnPredicate = z.object({
  op: LumpColumnPredicateOpLiteral,
  arg: z.string(),
});
export type LumpColumnPredicate = z.infer<typeof LumpColumnPredicate>;

export type ColumnPredicateOp =
  | UnaryColumnPredicateOp
  | BinaryColumnPredicateOp
  | ListBinaryColumnPredicateOp
  | CompoundColumnPredicateOp
  | LumpColumnPredicateOp;

// have to manually declare this type since it is recursive
export type CompoundColumnPredicate = {
  op: CompoundColumnPredicateOp;
  args: ColumnPredicate[];
};
export const CompoundColumnPredicate: z.ZodType<
  CompoundColumnPredicate,
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z.object({
    op: CompoundColumnPredicateOpLiteral,
    args: z.array(ColumnPredicate),
  }),
);

export const ColumnPredicate = z.union([
  UnaryColumnPredicate,
  BinaryColumnPredicate,
  ListBinaryColumnPredicate,
  CompoundColumnPredicate,
  LumpColumnPredicate,
]);
export type ColumnPredicate =
  | z.infer<typeof ColumnPredicate>
  | CompoundColumnPredicate;

// Note: When we pass filters into the python kernel they must be converted to
// this internal representation which flags which filters allow Jinja
// interpolation. Filters configured by the app authors allow interpolation but
// additional filters configured by app users do not.

export function toInternalPredicate(
  predicate: ColumnPredicate,
  { allowInterpolation }: { allowInterpolation: boolean },
): ColumnPredicate {
  if (
    zGuard(UnaryColumnPredicate, predicate) ||
    zGuard(LumpColumnPredicate, predicate)
  ) {
    return predicate;
  } else if (zGuard(CompoundColumnPredicate, predicate)) {
    return {
      ...predicate,
      args: predicate.args.map((child) =>
        toInternalPredicate(child, { allowInterpolation }),
      ),
    };
  } else if (
    zGuard(BinaryColumnPredicate, predicate) ||
    zGuard(ListBinaryColumnPredicate, predicate)
  ) {
    return {
      ...predicate,
      allowInterpolation,
    };
  } else {
    guardNever(predicate, predicate);
    return predicate;
  }
}
